import type { LobbyCharacterInfo } from '@danda/shared';
import type { Character } from '../lobbyStore.types';

/**
 * Resolve avatar URL for a character from lobbyCharacters and Characters store.
 *
 * Priority: lobbyChar.avatarUrl > lobbyChar.avatar > storeChar.avatarUrl
 *         > storeChar.avatar > storeChar.permanent.avatar_url > storeChar.permanent.avatarUrl
 */
export function resolveCharacterAvatarUrl(
  lobbyChar: LobbyCharacterInfo | undefined,
  storeChar: Character | undefined
): string | undefined {
  return (
    lobbyChar?.avatarUrl ||
    lobbyChar?.avatar ||
    storeChar?.avatarUrl ||
    storeChar?.avatar ||
    (storeChar?.permanent?.avatar_url as string | undefined) ||
    (storeChar?.permanent?.avatarUrl as string | undefined) ||
    undefined
  );
}

/**
 * Resolve a full-body portrait URL for a character — same source
 * priority as `resolveCharacterAvatarUrl`. Used as a fallback when
 * the square `avatarUrl` isn't generated yet but a portrait exists
 * (e.g. PCs whose `session.avatar_url` is still null but whose
 * `session.portrait_url` was populated by portrait generation, or
 * roster NPCs that only carry a portrait).
 */
export function resolveCharacterPortraitUrl(
  lobbyChar: LobbyCharacterInfo | undefined,
  storeChar: Character | undefined
): string | undefined {
  return (
    lobbyChar?.portraitUrl ||
    storeChar?.portraitUrl ||
    (storeChar?.permanent?.portrait_url as string | undefined) ||
    (storeChar?.permanent?.portraitUrl as string | undefined) ||
    undefined
  );
}

/**
 * Resolve avatar emoji for a character from lobbyCharacters and Characters store.
 */
export function resolveCharacterAvatarEmoji(
  lobbyChar: LobbyCharacterInfo | undefined,
  storeChar: Character | undefined
): string | undefined {
  return (
    lobbyChar?.avatarEmoji ||
    storeChar?.avatarEmoji ||
    (storeChar?.permanent?.avatar_emoji as string | undefined) ||
    undefined
  );
}
